import * as Api from '@ViewModels';
import moment from 'moment';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { numberToUSDCurrencyStringValue } from '../../../../../models/UiUtils';
import {
	invalidateInsurancePolicyFilter,
	invalidateInsurancePolicySummary,
	useInsurancePolicyFilter,
	useInsurancePolicySummaryQuery,
} from '../../../../../queries';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../../components-v2/Dropdown';
import { Pill } from '../../../../components-v2/Pill';
import { THEME } from '../../../../components-v2/Theme/theme';
import { PolicyIcon } from '../../../svgs/icons/PolicyIcon';
import { DataBoardRow, DataBoardTable, pillColorFromHex } from '../../DataBoardTable';
import { DeleteModal, DeleteType } from '../../DeleteModal';
import { EditPolicyModal } from '../EditPolicyModal';

const formatDate = (dateString?: string) => (dateString ? moment(dateString).format('MM/DD/YYYY') : '-');

const formatUserName = (user?: Api.IUserReference) =>
	user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || '-' : '-';

const formatCurrency = (value?: number) => (value != null ? numberToUSDCurrencyStringValue(value) : '-');

export const EntityPolicies = ({ entity }: { entity: Api.IEntity }) => {
	const userSession = useUserSession();
	const canEdit = !userSession.hasInsuranceIntegrationConfigured;

	const [policyToEdit, setPolicyToEdit] = React.useState<Api.IInsurancePolicy | null>(null);
	const [policyToDelete, setPolicyToDelete] = React.useState<Api.IInsurancePolicy | null>(null);

	const filterRequest: Api.IInsurancePolicyFilterRequest = {
		criteria: [
			{
				property: Api.InsurancePolicyFilterProperty.Contact,
				value: entity.id,
			},
		],
	};

	const policyFilterQuery = useInsurancePolicyFilter({ enabled: !!entity.id, filterRequest });
	const summaryQuery = useInsurancePolicySummaryQuery({ enabled: !!entity.id, filterRequest });

	const policies = React.useMemo(() => {
		return policyFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [policyFilterQuery.data]);

	const onFetchNextBatchOfPolicies = () => {
		if (policyFilterQuery.hasNextPage && !policyFilterQuery.isFetchingNextPage) {
			policyFilterQuery.fetchNextPage();
		}
	};

	const refreshPolicies = () => {
		setPolicyToEdit(null);
		setPolicyToDelete(null);
		invalidateInsurancePolicyFilter({ filterRequest });
		invalidateInsurancePolicySummary({ filterRequest });
	};

	const totalPolicies = summaryQuery.data?.totalPolicies ?? 0;
	const summary = totalPolicies
		? `${totalPolicies} ${totalPolicies === 1 ? 'policy' : 'policies'} · ${numberToUSDCurrencyStringValue(
				summaryQuery.data?.totalAmount ?? 0
			)} total premium`
		: undefined;

	const rows: DataBoardRow[] = policies.map(policy => ({
		id: policy.id,
		name: [policy.lineOfBusiness?.name, policy.carrier?.name].filter(Boolean).join(' · ') || 'Policy',
		statusTitle: policy.lineOfBusiness?.name,
		status: policy.lineOfBusiness ? (
			<Pill
				variant='label'
				color={pillColorFromHex(policy.lineOfBusiness.foregroundColor)}
				label={policy.lineOfBusiness.name}
			/>
		) : (
			'-'
		),
		fields: [policy.policyNumber || '-', formatDate(policy.renewalDate), formatUserName(policy.owner)],
		value: formatCurrency(policy.premium),
		details: [
			{ label: 'Effective date', value: formatDate(policy.effectiveDate) },
			{ label: 'Origination date', value: formatDate(policy.originationDate) },
			{ label: 'Carrier', value: policy.carrier?.name || '-' },
			{ label: 'Commissions', value: formatCurrency(policy.commissions) },
			{ label: 'CSR', value: formatUserName(policy.servicingRole1User) },
			{ label: 'Producer', value: formatUserName(policy.servicingRole2User) },
		],
		actions: canEdit ? (
			<Dropdown>
				<DropdownEllipsisTrigger aria-label={`Actions for policy ${policy.policyNumber ?? ''}`.trim()} />
				<DropdownContent>
					<DropdownItem onClick={() => setPolicyToEdit(policy)}>Edit</DropdownItem>
					<DropdownItem onClick={() => setPolicyToDelete(policy)}>Delete</DropdownItem>
				</DropdownContent>
			</Dropdown>
		) : undefined,
	}));

	return (
		<div>
			<DataBoardTable
				icon={<PolicyIcon fillColor={THEME.colors.primary.contrastText} />}
				iconColor='blue'
				title='Policies'
				summary={summary}
				addItemName='Policy'
				addDisabledReason={canEdit ? undefined : 'Policies sync from your insurance integration, so add them there.'}
				onAdd={() => setPolicyToEdit({ contact: entity } as Api.IInsurancePolicy)}
				nameLabel='Policy'
				statusLabel='Line'
				fieldLabels={['Policy #', 'Renewal', 'Owner']}
				valueLabel='Premium'
				rows={rows}
				isLoading={policyFilterQuery.isPending}
				emptyText={
					policyFilterQuery.isError ? 'Couldn’t load this contact’s policies.' : 'No policies exist for this contact.'
				}
			>
				{policies.length ? <Waypoint bottomOffset='-200px' onEnter={onFetchNextBatchOfPolicies} /> : null}
			</DataBoardTable>

			{policyToEdit ? (
				<EditPolicyModal
					initialPolicy={policyToEdit}
					isOpen={!!policyToEdit}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setPolicyToEdit(null);
						}
					}}
					onSave={refreshPolicies}
					onCancel={() => setPolicyToEdit(null)}
				/>
			) : null}

			{policyToDelete ? (
				<DeleteModal
					type={DeleteType.Policy}
					item={policyToDelete}
					isOpen={!!policyToDelete}
					setIsOpen={isOpen => {
						if (!isOpen) {
							setPolicyToDelete(null);
						}
					}}
					onConfirm={refreshPolicies}
				/>
			) : null}
		</div>
	);
};
